export const heroData = {
  title: "AIRDOA Articles",
  subtitle:
    "From Yokohama date ideas and helicopter tour tips to gift inspiration. Engaging articles to guide your booking.",
  image:
    "https://readdy.ai/api/search-image?query=Elegant%20open%20notebook%20with%20handwritten%20notes%20and%20fountain%20pen%20on%20dark%20navy%20marble%20surface%2C%20soft%20warm%20ambient%20lighting%2C%20scattered%20dried%20flowers%2C%20luxury%20lifestyle%20editorial%20photography%2C%20champagne%20gold%20accents%2C%20minimalist%20composition%2C%20no%20text%2C%20high%20quality&width=1920&height=600&seq=colhero02&orientation=landscape",
};

export const categories = [
  { id: "all", label: "All" },
  { id: "date", label: "Date & Anniversary" },
  { id: "propose", label: "Proposal" },
  { id: "beginner", label: "First-Time Visitors" },
  { id: "guide", label: "Experience Guide" },
  { id: "planning", label: "Attire & Packing" },
  { id: "weather", label: "Weather & Cancellations" },
  { id: "access", label: "Access" },
  { id: "gift", label: "Gift" },
  { id: "rule", label: "Heli Tour Rules" },
  { id: "compare", label: "View Comparisons" },
];

export interface ColumnBlock {
  type: "heading" | "paragraph" | "image" | "cta";
  value: string;
}

export interface ColumnArticle {
  slug: string;
  title: string;
  category: string;
  categoryId: string;
  date: string;
  updatedDate: string;
  readTime: string;
  excerpt: string;
  image: string;
  heroImage: string;
  isPopular: boolean;
  isFeatured: boolean;
  isNew: boolean;
  toc: string[];
  content: ColumnBlock[];
}

export const columns: ColumnArticle[] = [
  {
    slug: "yokohama-heli-date-tips",
    title: "5 Tips for a Perfect Yokohama Helicopter Date",
    category: "Date & Anniversary",
    categoryId: "date",
    date: "2025.05.01",
    updatedDate: "2025.05.01",
    readTime: "6 min read",
    excerpt:
      "Tips to make your Yokohama helicopter date even more special — from attire to timing. Expert advice for a flawless experience.",
    image:
      "https://readdy.ai/api/search-image?query=Couple%20silhouette%20against%20panoramic%20Yokohama%20night%20skyline%20from%20helicopter%20window%20view%2C%20romantic%20date%20atmosphere%2C%20warm%20golden%20city%20lights%20reflecting%20on%20water%2C%20deep%20navy%20sky%2C%20luxury%20travel%20editorial%20photography%2C%20no%20text&width=640&height=420&seq=colimg01&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Couple%20silhouette%20against%20panoramic%20Yokohama%20night%20skyline%20from%20helicopter%20window%20view%2C%20romantic%20date%20atmosphere%2C%20warm%20golden%20city%20lights%20reflecting%20on%20water%2C%20deep%20navy%20sky%2C%20luxury%20travel%20editorial%20photography%2C%20no%20text&width=1920&height=600&seq=colhero01&orientation=landscape",
    isPopular: true,
    isFeatured: true,
    isNew: true,
    toc: [
      "Choosing the right time slot",
      "Dress smart, don't stress",
      "Check the weather and set a backup date",
      "Plan a surprise inside the cabin",
      "Capture the memory with photos",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "A helicopter date in Yokohama is a special experience like no other. But to make it truly unforgettable, there are a few things worth knowing. Here are five expert tips for a date that will leave a lasting impression.",
      },
      {
        type: "heading",
        value: "Choosing the right time slot",
      },
      {
        type: "paragraph",
        value:
          "The night view course is the most popular, but the sunset course is equally romantic. The magic hour just before and after sunset — when the sky shifts from orange to pink to deep navy — is an extraordinary time of day. While many guests choose the night view for a date finale, the course that lets you experience the transition from sunset to night is widely considered the most romantic of all.",
      },
      {
        type: "image",
        value:
          "https://readdy.ai/api/search-image?query=Yokohama%20Minato%20Mirai%20skyline%20at%20magic%20hour%20sunset%20with%20orange%20and%20deep%20navy%20gradient%20sky%2C%20warm%20ambient%20light%20reflecting%20on%20bay%20water%2C%20romantic%20panoramic%20view%2C%20editorial%20travel%20photography%2C%20no%20text&width=900&height=500&seq=colbody01&orientation=landscape",
      },
      {
        type: "heading",
        value: "Dress smart, don't stress",
      },
      {
        type: "paragraph",
        value:
          "Since the helipad is outdoors, dressing for the season is important. Skirts and heels are fine, but avoid very thin heels — stable footwear is recommended. The cabin has heating and air conditioning, but the area near the windows can be affected by outside temperatures. Bringing an extra layer to put on is a good idea. For a romantic date, dressing up a little adds to the atmosphere.",
      },
      {
        type: "heading",
        value: "Check the weather and set a backup date",
      },
      {
        type: "paragraph",
        value:
          "Helicopter tours are heavily influenced by weather. Always check the forecast on the day and we strongly recommend setting one or two backup dates. In rain or strong winds, flights are cancelled for safety reasons, but AIRDOA offers rescheduling or a full refund. Having a backup date keeps any disappointment on the day to a minimum.",
      },
      {
        type: "heading",
        value: "Plan a surprise inside the cabin",
      },
      {
        type: "paragraph",
        value:
          "For anniversaries or proposals, presenting a gift inside the cabin is absolutely possible. Talk to our staff in advance and we will help you time it perfectly. Keep in mind the cabin can be noisy, so speaking up or preparing a written message card makes things go smoothly.",
      },
      {
        type: "cta",
        value: "yokohama",
      },
      {
        type: "heading",
        value: "Capture the memory with photos",
      },
      {
        type: "paragraph",
        value:
          "A commemorative photo before or after boarding — with the helicopter as your backdrop — is the perfect shot. Standing together in front of the aircraft makes a great social media moment. A professional photographer option is also available, with digital data delivered afterwards. For a special date, we highly recommend adding the photo session.",
      },
      {
        type: "paragraph",
        value:
          "A Yokohama helicopter date begins the moment you start planning. Follow these tips and create the most magical date of your lives.",
      },
    ],
  },
  {
    slug: "heli-what-to-bring",
    title: "What to Wear & Bring: A Complete Guide to Your Helicopter Tour",
    category: "Attire & Packing",
    categoryId: "planning",
    date: "2025.04.28",
    updatedDate: "2025.04.28",
    readTime: "5 min read",
    excerpt:
      "First time on a helicopter tour? Not sure what to wear or bring? This complete guide answers all your questions before you arrive.",
    image:
      "https://readdy.ai/api/search-image?query=Elegant%20navy%20blue%20and%20champagne%20gold%20evening%20dress%20accessories%20laid%20out%20on%20minimal%20cream%20surface%2C%20luxury%20fashion%20editorial%20photography%2C%20soft%20natural%20lighting%2C%20sophisticated%20minimalist%20composition%2C%20no%20text&width=640&height=420&seq=colimg02&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Elegant%20navy%20blue%20and%20champagne%20gold%20evening%20dress%20accessories%20laid%20out%20on%20minimal%20cream%20surface%2C%20luxury%20fashion%20editorial%20photography%2C%20soft%20natural%20lighting%2C%20sophisticated%20minimalist%20composition%2C%20no%20text&width=1920&height=600&seq=colhero02&orientation=landscape",
    isPopular: true,
    isFeatured: false,
    isNew: true,
    toc: [
      "Basic attire rules",
      "Season-by-season attire advice",
      "Packing checklist",
      "Making the most of your time in the cabin",
      "What not to wear or bring",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "If you are preparing for your first helicopter tour, you may be wondering: 'What should I wear?' or 'Can I bring a bag?' Here is a clear and detailed guide to attire and what to bring.",
      },
      {
        type: "heading",
        value: "Basic attire rules",
      },
      {
        type: "paragraph",
        value:
          "There are no strict dress code rules for a helicopter tour. Comfortable everyday clothing is perfectly fine. However, since the helipad is outdoors, having a jacket or extra layer for the season is recommended. Items such as hats and scarves that can be easily blown away may need to be temporarily stored when boarding.",
      },
      {
        type: "heading",
        value: "Season-by-season attire advice",
      },
      {
        type: "paragraph",
        value:
          "In spring and autumn, temperatures can vary significantly between day and evening, so always bring something to layer. In summer, short sleeves are comfortable in the cabin, but consider the waiting time at the helipad and pack sunscreen. In winter, a coat is essential — footwear that handles rain and snow is also advisable.",
      },
      {
        type: "heading",
        value: "Packing checklist",
      },
      {
        type: "paragraph",
        value:
          "The two essentials are a photo ID and your smartphone (or camera). Bags cannot be taken into the cabin, so pare down to just the essentials. Valuables should be kept on your person — a storage bag at check-in is also available.",
      },
      {
        type: "heading",
        value: "Making the most of your time in the cabin",
      },
      {
        type: "paragraph",
        value:
          "The cabin has air conditioning, but the area near the windows can be affected by outside temperatures — an extra layer is reassuring. If you plan to take lots of photos, make sure your smartphone battery is fully charged before you arrive.",
      },
      {
        type: "heading",
        value: "What not to wear or bring",
      },
      {
        type: "paragraph",
        value:
          "Sandals and flip-flops should be avoided for safety reasons at the helipad. Large bags, tripods, and selfie sticks cannot be brought into the cabin. Drones and other filming equipment are also prohibited.",
      },
      {
        type: "paragraph",
        value:
          "Once you have sorted your attire and packing, you can focus entirely on enjoying the views on the day. Use this guide to prepare and arrive with confidence.",
      },
    ],
  },
  {
    slug: "best-time-for-heli-tour",
    title: "Night View vs Sunset vs Daytime — Which Time Slot Is Best?",
    category: "View Comparisons",
    categoryId: "compare",
    date: "2025.04.25",
    updatedDate: "2025.04.25",
    readTime: "7 min read",
    excerpt:
      "The hardest decision when booking a helicopter tour is often which time slot to choose. We compare the appeal of each — night view, sunset, and daytime — to help you find the perfect fit.",
    image:
      "https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20Yokohama%20bay%20bridge%20and%20Minato%20Mirai%20skyline%20showing%20three%20time%20zones%20side%20by%20side%2C%20daytime%20blue%20sky%20sunset%20orange%20and%20night%20deep%20navy%20with%20city%20lights%2C%20editorial%20travel%20photography%2C%20no%20text&width=640&height=420&seq=colimg03&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Aerial%20panoramic%20view%20of%20Yokohama%20bay%20bridge%20and%20Minato%20Mirai%20skyline%20showing%20three%20time%20zones%20side%20by%20side%2C%20daytime%20blue%20sky%20sunset%20orange%20and%20night%20deep%20navy%20with%20city%20lights%2C%20editorial%20travel%20photography%2C%20no%20text&width=1920&height=600&seq=colhero03&orientation=landscape",
    isPopular: true,
    isFeatured: true,
    isNew: false,
    toc: [
      "Daytime: Blue sky and the sea",
      "Sunset: The romance of magic hour",
      "Night view: A jewel box of light",
      "Pricing and booking difficulty by time slot",
      "How to choose the right time slot for you",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "One of the most common dilemmas when booking a helicopter tour is choosing the time slot. Night view, sunset, daytime — each has its own distinct appeal, and the best choice depends on your purpose and budget.",
      },
      {
        type: "heading",
        value: "Daytime: Blue sky and the sea",
      },
      {
        type: "paragraph",
        value:
          "The vibrant contrast of the blue sky, the sea, lush parks, and bright white ships is at its best in daylight. This is the ideal time for photography, and on a clear day you can even spot Mt. Fuji in the distance. Best for sightseeing, families, and those who want to take plenty of photos — and generally the most affordable.",
      },
      {
        type: "heading",
        value: "Sunset: The romance of magic hour",
      },
      {
        type: "paragraph",
        value:
          "The magic hour just before and after sunset. The sky shifts from orange to deep navy in the most romantic light of the day. Ideal for dates and proposals. The sunset course is popular, so booking 1–2 months in advance is best.",
      },
      {
        type: "heading",
        value: "Night view: A jewel box of light",
      },
      {
        type: "paragraph",
        value:
          "The glittering night skyline that defines Yokohama. The panorama of Bay Bridge and Minato Mirai lit up at night delivers emotion that simply cannot be found on the ground. Top choice for dates, anniversaries, and gifts — and the most popular time slot, so booking 1–2 months ahead is ideal.",
      },
      {
        type: "heading",
        value: "Pricing and booking difficulty by time slot",
      },
      {
        type: "paragraph",
        value:
          "Pricing varies by time slot: night view is the highest, daytime is the most affordable, and sunset falls in between. In terms of booking difficulty, night view and sunset are the hardest to secure, while daytime is relatively easier to book.",
      },
      {
        type: "heading",
        value: "How to choose the right time slot for you",
      },
      {
        type: "paragraph",
        value:
          "Daytime is best for families and sightseeing. For dates and anniversaries, go for sunset or night view. If you want to keep costs down, daytime is the way to go. For the most spectacular experience, night view wins. If you are unsure, our staff at AIRDOA are happy to help you decide.",
      },
    ],
  },
  {
    slug: "heli-proposal-why",
    title: "Why Choose a Helicopter Tour for Your Proposal?",
    category: "Proposal",
    categoryId: "propose",
    date: "2025.04.20",
    updatedDate: "2025.04.20",
    readTime: "5 min read",
    excerpt:
      "Why are so many people choosing helicopter tours for their proposals? We explore the unique magic of this extraordinary setting.",
    image:
      "https://readdy.ai/api/search-image?query=Romantic%20diamond%20engagement%20ring%20in%20luxury%20velvet%20box%20on%20dark%20navy%20surface%2C%20warm%20golden%20ambient%20lighting%2C%20soft%20bokeh%20city%20lights%20background%2C%20luxury%20proposal%20concept%2C%20editorial%20jewelry%20photography%2C%20no%20text&width=640&height=420&seq=colimg04&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Romantic%20diamond%20engagement%20ring%20in%20luxury%20velvet%20box%20on%20dark%20navy%20surface%2C%20warm%20golden%20ambient%20lighting%2C%20soft%20bokeh%20city%20lights%20background%2C%20luxury%20proposal%20concept%2C%20editorial%20jewelry%20photography%2C%20no%20text&width=1920&height=600&seq=colhero04&orientation=landscape",
    isPopular: true,
    isFeatured: false,
    isNew: false,
    toc: [
      "An extraordinary setting for an extraordinary moment",
      "An intimate private space",
      "Yokohama's night view as the ultimate backdrop",
      "Peace of mind with pre-flight consultation",
      "Tips for a successful proposal",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "Restaurants and hotels have long been the classic proposal setting, but in recent years, helicopter tour proposals have soared in popularity. Here is why — with real examples to bring the idea to life.",
      },
      {
        type: "heading",
        value: "An extraordinary setting for an extraordinary moment",
      },
      {
        type: "paragraph",
        value:
          "A proposal in a place that everyday life simply cannot offer. The breathtaking view from the sky becomes the perfect backdrop for the most important moment of two people's lives. An experience that is truly out of this world — and a surprise your partner will never forget.",
      },
      {
        type: "heading",
        value: "An intimate private space",
      },
      {
        type: "paragraph",
        value:
          "The helicopter cabin is an intimate space that feels entirely your own. You can speak your heart without worrying about other guests around you. Unlike a busy restaurant, this is a world of your own — the perfect place to say what needs to be said.",
      },
      {
        type: "heading",
        value: "Yokohama's night view as the ultimate backdrop",
      },
      {
        type: "paragraph",
        value:
          "The night view woven by the Bay Bridge and Minato Mirai is the ultimate backdrop for a proposal. Saying 'Will you marry me?' surrounded by a sea of light outside the window creates an emotion that words cannot capture.",
      },
      {
        type: "cta",
        value: "yokohama",
      },
      {
        type: "heading",
        value: "Peace of mind with pre-flight consultation",
      },
      {
        type: "paragraph",
        value:
          "AIRDOA carefully coordinates every detail of your proposal in advance. Timing the ring presentation, the best moment for photos, music requests — we accommodate even the smallest wishes.",
      },
      {
        type: "heading",
        value: "Tips for a successful proposal",
      },
      {
        type: "paragraph",
        value:
          "The cabin can be noisy, so preparing a written message card is recommended. Also, given the risk of a weather cancellation, setting a backup date brings peace of mind. The key to success is preparation — and love.",
      },
    ],
  },
  {
    slug: "gift-ticket-guide",
    title: "How to Choose a Helicopter Tour Gift Ticket",
    category: "Gift",
    categoryId: "gift",
    date: "2025.04.15",
    updatedDate: "2025.04.15",
    readTime: "5 min read",
    excerpt:
      "A helicopter tour gift ticket for someone you love. A guide to choosing the right option, and ideas for how to present it.",
    image:
      "https://readdy.ai/api/search-image?query=Luxury%20gift%20box%20with%20champagne%20gold%20ribbon%20and%20handwritten%20card%20on%20dark%20navy%20marble%20surface%2C%20warm%20ambient%20lighting%2C%20premium%20gift%20presentation%2C%20elegant%20minimalist%20editorial%20photography%2C%20no%20text&width=640&height=420&seq=colimg05&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Luxury%20gift%20box%20with%20champagne%20gold%20ribbon%20and%20handwritten%20card%20on%20dark%20navy%20marble%20surface%2C%20warm%20ambient%20lighting%2C%20premium%20gift%20presentation%2C%20elegant%20minimalist%20editorial%20photography%2C%20no%20text&width=1920&height=600&seq=colhero05&orientation=landscape",
    isPopular: true,
    isFeatured: true,
    isNew: false,
    toc: [
      "Types of gift tickets",
      "Choosing the right course for your recipient",
      "Message card and wrapping",
      "Surprise presentation ideas",
      "Using the booking assistance service",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "Helicopter tour gift tickets are becoming increasingly popular as a truly special present. Here is a practical guide — from choosing the right ticket to the best way to hand it over.",
      },
      {
        type: "heading",
        value: "Types of gift tickets",
      },
      {
        type: "paragraph",
        value:
          "AIRDOA offers course-specific gift tickets. Choose from the 15-minute short course to the 30-minute premium course based on budget and purpose. Each ticket has a validity period, allowing the recipient to book at a time that suits them.",
      },
      {
        type: "heading",
        value: "Choosing the right course for your recipient",
      },
      {
        type: "paragraph",
        value:
          "For a light introduction, the 15-minute short course is ideal. For a fuller experience, the 25-minute standard course is recommended. For an anniversary or proposal, the 30-minute sunset + night view premium course is the ultimate choice. Think about the recipient's lifestyle when deciding.",
      },
      {
        type: "heading",
        value: "Message card and wrapping",
      },
      {
        type: "paragraph",
        value:
          "We print a message from the giver on the card and include it in the gift. A heartfelt message makes the gift even more special. Our exclusive gift wrapping adds magic from the very moment of unboxing.",
      },
      {
        type: "heading",
        value: "Surprise presentation ideas",
      },
      {
        type: "paragraph",
        value:
          "Hide the ticket inside a birthday cake, pair it with a bouquet of flowers, tuck it into an anniversary letter — the presentation is entirely up to you. The ticket itself is already a surprise, so however you give it, the emotion is guaranteed.",
      },
      {
        type: "cta",
        value: "gift",
      },
      {
        type: "heading",
        value: "Using the booking assistance service",
      },
      {
        type: "paragraph",
        value:
          "To save the recipient the effort of booking themselves, the giver can use our booking assistance service. If the date is already decided, we can handle the reservation on your behalf — and the recipient simply shows up at the helipad on the day.",
      },
    ],
  },
  {
    slug: "rainy-day-policy",
    title: "What Happens When a Flight Is Cancelled Due to Weather?",
    category: "Weather & Cancellations",
    categoryId: "weather",
    date: "2025.04.10",
    updatedDate: "2025.04.10",
    readTime: "4 min read",
    excerpt:
      "Cancellations are made with safety as the top priority. Here is a detailed explanation of what happens if a flight cannot go ahead due to weather on the day.",
    image:
      "https://readdy.ai/api/search-image?query=Dramatic%20dark%20storm%20clouds%20over%20calm%20ocean%20with%20distant%20city%20skyline%20silhouette%2C%20moody%20atmospheric%20weather%20scene%2C%20deep%20navy%20and%20gray%20tones%2C%20editorial%20nature%20photography%2C%20no%20text&width=640&height=420&seq=colimg06&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Dramatic%20dark%20storm%20clouds%20over%20calm%20ocean%20with%20distant%20city%20skyline%20silhouette%2C%20moody%20atmospheric%20weather%20scene%2C%20deep%20navy%20and%20gray%20tones%2C%20editorial%20nature%20photography%2C%20no%20text&width=1920&height=600&seq=colhero06&orientation=landscape",
    isPopular: false,
    isFeatured: false,
    isNew: false,
    toc: [
      "Cancellation criteria",
      "How to check weather on the day",
      "Rescheduling and refunds after cancellation",
      "Why setting a backup date matters",
      "How to reduce anxiety",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "Helicopter tours are heavily influenced by weather conditions. Here is a clear explanation of the cancellation criteria and what happens afterwards.",
      },
      {
        type: "heading",
        value: "Cancellation criteria",
      },
      {
        type: "paragraph",
        value:
          "Poor visibility, strong winds, thunderstorms, and dense fog — when any of these conditions fall below the operating threshold, the operator will make the call to cancel. Decisions are made right up until just before boarding, always with guest safety as the highest priority.",
      },
      {
        type: "heading",
        value: "How to check weather on the day",
      },
      {
        type: "paragraph",
        value:
          "From the evening before, AIRDOA will contact you by email or phone with a weather update. We will also send the latest information on the morning of the day, and if there is a possibility of cancellation, we will notify you as early as possible.",
      },
      {
        type: "heading",
        value: "Rescheduling and refunds after cancellation",
      },
      {
        type: "paragraph",
        value:
          "If a flight is cancelled, you can choose either a reschedule or a full refund. For rescheduling, we will check availability on your preferred date and give you priority. Refunds are processed within one week of the request.",
      },
      {
        type: "heading",
        value: "Why setting a backup date matters",
      },
      {
        type: "paragraph",
        value:
          "Having one or two backup dates ready keeps any disappointment from a cancellation to a minimum. For guests visiting Yokohama for sightseeing, we recommend building some flexibility into your schedule.",
      },
      {
        type: "heading",
        value: "How to reduce anxiety",
      },
      {
        type: "paragraph",
        value:
          "No one can predict a cancellation. But AIRDOA keeps a constant eye on weather changes and gets information to guests as early as possible. Arrive on the day with a calm mind, knowing we have everything covered.",
      },
    ],
  },
  {
    slug: "heli-vs-other-experiences",
    title: "Helicopter Tour vs Other Experiences — A Comparison Guide",
    category: "Experience Guide",
    categoryId: "guide",
    date: "2025.04.05",
    updatedDate: "2025.04.05",
    readTime: "6 min read",
    excerpt:
      "How does a helicopter tour compare to a cruise, a hotel dinner, or a skyscraper observation deck? We explore the unique value that only the sky can offer.",
    image:
      "https://readdy.ai/api/search-image?query=Luxury%20yacht%20on%20calm%20water%20at%20twilight%20with%20illuminated%20city%20skyline%20reflection%2C%20deep%20navy%20sky%2C%20romantic%20cruise%20atmosphere%2C%20editorial%20travel%20photography%2C%20no%20text&width=640&height=420&seq=colimg07&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Luxury%20yacht%20on%20calm%20water%20at%20twilight%20with%20illuminated%20city%20skyline%20reflection%2C%20deep%20navy%20sky%2C%20romantic%20cruise%20atmosphere%2C%20editorial%20travel%20photography%2C%20no%20text&width=1920&height=600&seq=colhero07&orientation=landscape",
    isPopular: false,
    isFeatured: false,
    isNew: false,
    toc: [
      "Compared to a cruise",
      "Compared to a hotel dinner",
      "Compared to a skyscraper observation deck",
      "The unique value of a helicopter tour",
      "Balancing cost and experiential value",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "For a special occasion or date, a helicopter tour holds a distinct value that sets it apart from other experiences. Here is how it compares to some of the most popular alternatives.",
      },
      {
        type: "heading",
        value: "Compared to a cruise",
      },
      {
        type: "paragraph",
        value:
          "A cruise also offers beautiful water views, but a helicopter gives you a 360-degree panorama from the sky. You can cover more ground in less time with greater freedom of movement. The shorter duration also makes it easier to fit around your plans.",
      },
      {
        type: "heading",
        value: "Compared to a hotel dinner",
      },
      {
        type: "paragraph",
        value:
          "A hotel dinner offers views from high up in a skyscraper, but a helicopter takes you even higher — with dynamic, ever-changing scenery as you weave between buildings. The focus on the experience itself, rather than a meal, is also a unique advantage.",
      },
      {
        type: "heading",
        value: "Compared to a skyscraper observation deck",
      },
      {
        type: "paragraph",
        value:
          "An observation deck offers a fixed view from one point. A helicopter moves — so you can see Landmark Tower up close, then pass over the Bay Bridge. That kind of ever-changing experience is simply not possible from a stationary platform.",
      },
      {
        type: "heading",
        value: "The unique value of a helicopter tour",
      },
      {
        type: "paragraph",
        value:
          "The greatest value of a helicopter tour lies in the extraordinary private experience. No other guests to worry about — just the two of you, with the entire view to yourselves. This is something no ground-level experience can replicate.",
      },
      {
        type: "heading",
        value: "Balancing cost and experiential value",
      },
      {
        type: "paragraph",
        value:
          "In terms of price, it is roughly comparable to or slightly higher than a hotel dinner. But the experiential value is in a completely different league. For a special anniversary or a memory that lasts a lifetime, a helicopter tour is the answer.",
      },
    ],
  },
  {
    slug: "heli-first-time-guide",
    title: "First Time on a Helicopter Tour? A Complete Guide to Easing Every Worry",
    category: "First-Time Visitors",
    categoryId: "beginner",
    date: "2025.03.28",
    updatedDate: "2025.05.01",
    readTime: "8 min read",
    excerpt:
      "Everything you need to know to feel completely at ease for your first helicopter tour — from the day-of schedule to what it's like inside the cabin.",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20helicopter%20on%20clean%20helipad%20with%20soft%20warm%20sunset%20lighting%2C%20professional%20aviation%20scene%2C%20elegant%20design%2C%20deep%20navy%20sky%20gradient%2C%20editorial%20transportation%20photography%2C%20no%20text&width=640&height=420&seq=colimg08&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Modern%20helicopter%20on%20clean%20helipad%20with%20soft%20warm%20sunset%20lighting%2C%20professional%20aviation%20scene%2C%20elegant%20design%2C%20deep%20navy%20sky%20gradient%2C%20editorial%20transportation%20photography%2C%20no%20text&width=1920&height=600&seq=colhero08&orientation=landscape",
    isPopular: true,
    isFeatured: false,
    isNew: false,
    toc: [
      "What is a helicopter sightseeing tour?",
      "The day-of schedule",
      "Inside the cabin",
      "Addressing safety concerns",
      "From booking to boarding",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "'Aren't helicopters dangerous?' 'What do I do if it's my first time?' — if these questions are on your mind, this guide is for you. Here is everything you need to know, from what happens on the day to how the booking process works.",
      },
      {
        type: "heading",
        value: "What is a helicopter sightseeing tour?",
      },
      {
        type: "paragraph",
        value:
          "A helicopter sightseeing tour is an experience where you board a helicopter and soar above a city or natural landscape. In Yokohama, you can enjoy breathtaking aerial views of Minato Mirai, the Bay Bridge, and the Red Brick Warehouse. The duration is approximately 15 to 30 minutes — brief, but enough for a memory that lasts a lifetime.",
      },
      {
        type: "heading",
        value: "The day-of schedule",
      },
      {
        type: "paragraph",
        value:
          "Arrive at the helipad 30 minutes before boarding. After check-in and the safety briefing, you will be guided to the aircraft by the pilot. Fasten your seatbelt, and you are off — the aerial adventure begins. After landing, there is an optional commemorative photo before you say goodbye. The whole process takes about an hour.",
      },
      {
        type: "heading",
        value: "Inside the cabin",
      },
      {
        type: "paragraph",
        value:
          "The cabin comfortably seats 2–3 guests. The windows are large, so every seat offers a spectacular view. The pilot will point out key landmarks as you fly, so you will not miss a thing. Smartphone photography is also permitted.",
      },
      {
        type: "heading",
        value: "Addressing safety concerns",
      },
      {
        type: "paragraph",
        value:
          "All partnered operators are MLIT-certified. Regular maintenance and experienced pilots ensure your safety. Flights are cancelled in bad weather. Statistically, helicopters are among the safest modes of transport.",
      },
      {
        type: "heading",
        value: "From booking to boarding",
      },
      {
        type: "paragraph",
        value:
          "Submit your preferred date online → receive a plan recommendation from our staff → confirm your booking → receive a pre-departure guide email → head to the helipad on the day. AIRDOA guides you every step of the way, so first-timers can relax and enjoy the ride.",
      },
      {
        type: "cta",
        value: "yokohama",
      },
    ],
  },
  {
    slug: "helipad-access-guide",
    title: "Complete Access Guide to the Yokohama Helipad",
    category: "Access",
    categoryId: "access",
    date: "2025.03.20",
    updatedDate: "2025.03.20",
    readTime: "4 min read",
    excerpt:
      "How to get there by train, car, or taxi. Information about the area around the helipad and tips for a smooth arrival on the day.",
    image:
      "https://readdy.ai/api/search-image?query=Modern%20train%20station%20entrance%20at%20dusk%20with%20warm%20lighting%2C%20urban%20Japanese%20city%20atmosphere%2C%20clean%20architectural%20lines%2C%20deep%20navy%20sky%2C%20editorial%20transportation%20photography%2C%20no%20text&width=640&height=420&seq=colimg09&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Modern%20train%20station%20entrance%20at%20dusk%20with%20warm%20lighting%2C%20urban%20Japanese%20city%20atmosphere%2C%20clean%20architectural%20lines%2C%20deep%20navy%20sky%2C%20editorial%20transportation%20photography%2C%20no%20text&width=1920&height=600&seq=colhero09&orientation=landscape",
    isPopular: false,
    isFeatured: false,
    isNew: false,
    toc: [
      "Getting there by train",
      "Getting there by car",
      "Taxi and transfer options",
      "About the helipad area",
      "Access tips",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "The Yokohama helipad is accessible by train, car, or taxi. Here is a detailed guide to each option.",
      },
      {
        type: "heading",
        value: "Getting there by train",
      },
      {
        type: "paragraph",
        value:
          "Approximately 5–10 minutes by taxi from the nearest station. Walking is also possible, but please allow extra time. Detailed directions will be emailed to you after your reservation is confirmed.",
      },
      {
        type: "heading",
        value: "Getting there by car",
      },
      {
        type: "paragraph",
        value:
          "Parking is available near the helipad, but spaces are limited. If you plan to drive, please let us know at the time of booking. Parking must be reserved in advance.",
      },
      {
        type: "heading",
        value: "Taxi and transfer options",
      },
      {
        type: "paragraph",
        value:
          "Approximately 5–10 minutes by taxi from the nearest station. A pick-up service is also available as an optional add-on. Talk to us at the time of booking and we will suggest the best option.",
      },
      {
        type: "heading",
        value: "About the helipad area",
      },
      {
        type: "paragraph",
        value:
          "The helipad is located in a residential area. Please be considerate of local residents. As it is an outdoor facility, bring an umbrella on rainy days. A waiting area is available on site.",
      },
      {
        type: "heading",
        value: "Access tips",
      },
      {
        type: "paragraph",
        value:
          "Please arrive at least 30 minutes before your scheduled boarding time. If you are running late, please call us immediately. First-time visitors are encouraged to arrive 15 minutes early for extra peace of mind.",
      },
    ],
  },
  {
    slug: "night-view-comparison-yokohama",
    title: "Yokohama Night View: Helicopter Tour vs Ground-Level Spots",
    category: "View Comparisons",
    categoryId: "compare",
    date: "2025.03.15",
    updatedDate: "2025.04.01",
    readTime: "6 min read",
    excerpt:
      "If you want to experience Yokohama's night view, where is best? We compare a helicopter tour with popular ground-level spots and explore the different ways to enjoy each.",
    image:
      "https://readdy.ai/api/search-image?query=Yokohama%20Minato%20Mirai%20night%20view%20from%20high%20vantage%20point%20showing%20illuminated%20Landmark%20Tower%20and%20Bay%20Bridge%2C%20thousands%20of%20golden%20city%20lights%2C%20deep%20navy%20sky%2C%20editorial%20cityscape%20photography%2C%20no%20text&width=640&height=420&seq=colimg10&orientation=landscape",
    heroImage:
      "https://readdy.ai/api/search-image?query=Yokohama%20Minato%20Mirai%20night%20view%20from%20high%20vantage%20point%20showing%20illuminated%20Landmark%20Tower%20and%20Bay%20Bridge%2C%20thousands%20of%20golden%20city%20lights%2C%20deep%20navy%20sky%2C%20editorial%20cityscape%20photography%2C%20no%20text&width=1920&height=600&seq=colhero10&orientation=landscape",
    isPopular: true,
    isFeatured: false,
    isNew: false,
    toc: [
      "Ground-level night view spots",
      "The night view from a helicopter tour",
      "The emotion created by a different perspective",
      "Getting the best from each",
      "A plan that combines both",
    ],
    content: [
      {
        type: "paragraph",
        value:
          "Yokohama is one of Japan's top night view destinations. The ground-level views are stunning, but the view from the sky is in a class of its own. Here is a comparison of what each perspective has to offer.",
      },
      {
        type: "heading",
        value: "Ground-level night view spots",
      },
      {
        type: "paragraph",
        value:
          "Landmark Tower's Sky Garden, Marine Tower, Osanbashi Pier — there are many great spots on the ground for taking in Yokohama's night view. The advantage is a stable vantage point and plenty of time to linger and enjoy the scenery.",
      },
      {
        type: "heading",
        value: "The night view from a helicopter tour",
      },
      {
        type: "paragraph",
        value:
          "From a helicopter, rather than visiting each spot one by one, you experience everything in a single sweeping panorama. The thrill of passing right over the Bay Bridge is simply impossible to feel from the ground.",
      },
      {
        type: "heading",
        value: "The emotion created by a different perspective",
      },
      {
        type: "paragraph",
        value:
          "From the ground, you focus on one spot at a time. From the air, you take in the whole city at once. The connections invisible from below — the bridge, the city, the sea — become clear from the sky.",
      },
      {
        type: "heading",
        value: "Getting the best from each",
      },
      {
        type: "paragraph",
        value:
          "If photography is your priority, the ground spots are ideal. For an overwhelming sense of wonder, a helicopter tour wins. If budget matters, ground-level is the practical choice. For a special occasion, the helicopter is in a league of its own.",
      },
      {
        type: "heading",
        value: "A plan that combines both",
      },
      {
        type: "paragraph",
        value:
          "Enjoy the night view from the ground first, then plan a helicopter tour for a future anniversary — deepening the experience in stages is a wonderful approach. Or try a helicopter tour followed by a café to relive the memories. The combination is entirely up to you.",
      },
    ],
  },
];

export const columnCTA = {
  yokohama: {
    label: "Yokohama Heli Cruise",
    title: "Yokohama from the sky is beauty on another level entirely",
    desc: "Inspired by what you have read? Come and experience it for yourself. AIRDOA guides you from booking to boarding.",
    button: "View Yokohama Heli Cruise",
    href: "/yokohama",
  },
  gift: {
    label: "Gift Tickets",
    title: "Give the gift of a special experience to someone you love",
    desc: "For anniversaries and surprises — a helicopter tour gift ticket, beautifully wrapped and delivered.",
    button: "View Gift Tickets",
    href: "/gift",
  },
  faq: {
    label: "FAQ",
    title: "Still have questions? Check our FAQ",
    desc: "We answer common questions from first-time visitors in detail.",
    button: "View FAQ",
    href: "/faq",
  },
};

export const columnPageMeta = {
  featuredTitle: "Featured Articles",
  newTitle: "New Articles",
  popularTitle: "Popular Articles",
  recommendTitle: "Recommended",
  viewAll: "View All Articles",
  readMore: "Read More",
  minRead: "min read",
};
